import React, { useEffect, useState } from "react";
import { sdh_ruleVersions, sdh_activateVersion, type RuleVersion } from "../api/sdhAPI";
import { useAuth } from "../hooks/useAuth";
import RuleLogicView from "./RuleLogicView";

/**
 * Every version of a rule, with the content and the measurement that belong to THAT version, and the
 * ability to make an earlier one active again. Switching is append-only: it records a new version whose
 * content equals the chosen one, so the trail stays truthful rather than being rewound.
 */
const FIELDS: Array<[string, string]> = [
  ["constraint_family", "Rule type"],
  ["condition", "Condition (WHEN)"],
  ["predicate_template", "Predicate (THEN)"],
  ["expected_outcome", "Expected outcome"],
  ["business_reason", "Plain meaning"],
  ["severity", "Severity"],
];

export default function RuleVersionsPanel(
  { entityId, appNm, constraintId, ruleName, onClose, onChanged }:
  { entityId: string; appNm: string; constraintId: string; ruleName?: string;
    onClose: () => void; onChanged: (m: string) => void }
) {
  const { user } = useAuth();
  const [versions, setVersions] = useState<RuleVersion[]>([]);
  const [active, setActive] = useState<number | null>(null);
  const [busy, setBusy] = useState(true);
  const [working, setWorking] = useState(false);
  const [open, setOpen] = useState<Record<number, boolean>>({});
  const [cmp, setCmp] = useState<number | null>(null);

  const load = async () => {
    setBusy(true);
    try {
      const r = await sdh_ruleVersions(entityId, constraintId);
      setVersions(r.versions || []); setActive(r.active_version ?? null);
      const top = (r.versions || [])[0];
      if (top) setOpen({ [top.version]: true });
    } catch { setVersions([]); } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [entityId, constraintId]);   // eslint-disable-line

  const activate = async (v: number) => {
    if (!window.confirm(`Make v${v} the active rule? This is recorded as a new version — nothing is lost.`)) return;
    setWorking(true);
    try {
      const r = await sdh_activateVersion(entityId, appNm, constraintId, v,
        user?.full_name || user?.user_id || user?.email);
      if (r?.status !== "ok") { onChanged(`Could not activate v${v} (${r?.status || "error"}).`); return; }
      const m = r.measured;
      onChanged(`v${v} is now active (saved as v${r.version})` +
        (m?.status === "ok" ? ` — re-measured: flags ${m.violating} of ${m.applicable}.` : "."));
      await load();
    } catch { onChanged("Could not switch version"); }
    finally { setWorking(false); }
  };

  // ---------- comparison screen (UI v4): any two versions side by side ----------
  const vs = versions;
  const byNo = (n: number | null) => vs.find((v) => v.version === n);
  const [aNo, setANo] = useState<number | null>(null);
  const [bNo, setBNo] = useState<number | null>(null);
  useEffect(() => {
    if (!vs.length) return;
    const act = active ?? vs[vs.length - 1].version;
    const other = vs.find((v) => v.version !== act);
    setANo(act); setBNo(other ? other.version : act);
  }, [vs.length, active]);   // eslint-disable-line
  const A: any = byNo(aNo), B: any = byNo(bNo);
  // a version's content is its SNAPSHOT (rule_versioning.list_versions)
  const snap = (v: any) => v?.snapshot || v?.content || {};
  const lg = (v: any) => snap(v).logic || v?.logic || null;
  const imp = (v: any) => { const i = v?.impact || {}; return { flagged: i.violating ?? i.violations, rows: i.applicable ?? i.applicable_rows }; };
  const d = A && B ? diffCodes(lg(A), lg(B)) : null;
  // "same logic" only when BOTH versions carry logic (two missing logics are not "the same")
  const same = !!(A && B && lg(A) && lg(B) && canonL(lg(A)) === canonL(lg(B)));
  const ia = imp(A), ib = imp(B);
  const dd = (x: any, y: any) => (x == null || y == null) ? null : Number(y) - Number(x);
  const why = (v: any) => {
    const ct = String(v?.change_type || "");
    const T: Record<string, string> = { generated: "original rule", regenerated: "dq_dedups — the agent found a new version of the same rule",
      consolidated: "consolidation — fragments combined into one rule", sme_refinement: "SME refinement (refine with agent)",
      lov_update: "reference-data update", manual_edit: "manual edit", version_activation: "re-activation of an earlier version" };
    return T[ct] || ct || "—";
  };
  const T = (v: any, k: string) => { const x = snap(v)[k]; return x == null ? "" : typeof x === "object" ? JSON.stringify(x) : String(x); };
  return (
    <div className="vh-scrim" onClick={onClose}>
      <style>{VCSS}</style>
      <div className="vc" onClick={(e) => e.stopPropagation()}>
        <header className="vc-h"><div><div className="lbl">&gt; VERSION COMPARISON</div><b>{ruleName || constraintId}</b>
          <span className="sub">{vs.length} version(s) · active v{active ?? "?"}</span></div>
          <button className="x" onClick={onClose}>×</button></header>
        {busy ? <div className="vc-b mut">Loading versions…</div> : vs.length < 2 ? <div className="vc-b mut">Only one version — nothing to compare.</div> :
        <div className="vc-b">
          <div className="vc-pick">
            {[["Compare", aNo, setANo], ["with", bNo, setBNo]].map(([l, val, set]: any) => <label key={l}>{l}
              <select value={val ?? ""} onChange={(e) => set(Number(e.target.value))}>
                {vs.map((v) => <option key={v.version} value={v.version}>v{v.version}{v.version === active ? " (active)" : ""} — {why(v)}</option>)}
              </select></label>)}
          </div>

          <section className="vc-sec"><h4>What changes</h4>
            {same ? <p className="warn">Both versions execute <b>exactly the same logic</b> — only their texts differ.</p>
             : d && (d.added.length || d.removed.length) ? <div className="codes">
                {d.added.map((c) => <span key={"a" + c} className="code add">+{c}</span>)}
                {d.removed.map((c) => <span key={"r" + c} className="code rem">−{c}</span>)}
                <span className="mut">{d.kept} code(s) unchanged</span></div>
             : (!lg(A) || !lg(B)) ? <p className="mut">{!lg(A) && !lg(B) ? "Neither version stores executable logic (created before logic was recorded) — compare the conditions below." : "Only one version stores executable logic — compare below."}</p>
             : <p className="mut">The logic differs beyond its code list — compare the two below.</p>}
            <div className="two">
              {[[aNo, A], [bNo, B]].map(([no, v]: any) => <div key={String(no)}><em>v{no}{no === active ? " · active" : ""}</em>
                {lg(v) ? <RuleLogicView rule={{ logic: lg(v) }} compact />
                  : <div className="cond"><div><span className="mut">WHEN</span> {T(v, "condition") || "—"}</div><div><span className="mut">THEN</span> {T(v, "predicate_template") || "—"}</div></div>}</div>)}
            </div>
          </section>

          <section className="vc-sec"><h4>On your data</h4>
            <table className="vc-t"><thead><tr><th></th><th>v{aNo}</th><th>v{bNo}</th><th>change</th></tr></thead><tbody>
              <tr><td>rows it applies to</td><td>{fmtN(ia.rows)}</td><td>{fmtN(ib.rows)}</td><td>{sgn(dd(ia.rows, ib.rows))}{dd(ia.rows, ib.rows) ? (Number(dd(ia.rows, ib.rows)) < 0 ? " (narrower scope)" : " (wider scope)") : ""}</td></tr>
              <tr><td>rows flagged</td><td>{fmtN(ia.flagged)}</td><td>{fmtN(ib.flagged)}</td><td>{sgn(dd(ia.flagged, ib.flagged))}</td></tr>
            </tbody></table>
            {(ia.rows == null || ib.rows == null) && <p className="mut">A version without numbers has not been measured yet — it is measured when it becomes active.</p>}
          </section>

          <section className="vc-sec"><h4>Why each version exists</h4>
            <div className="two">{[A, B].map((v: any, i: number) => <div key={i} className="why">
              <b>v{v?.version}</b> {why(v)}
              {v?.relation && <div>relation: <span className="mono">{v.relation}</span></div>}
              {v?.superseded_before_review && <div className="warn">superseded by a newer proposal before review</div>}
              <div className="mut">{v?.author || v?.created_by || ""}{v?.at || v?.created_at ? ` · ${String(v.at || v.created_at).replace("T", " ").slice(0, 16)}` : ""}</div>
              {v?.summary && <div className="mut">{v.summary}</div>}
            </div>)}</div>
          </section>

          <details className="vc-sec"><summary>Texts (title, summary, meaning)</summary>
            <table className="vc-t"><thead><tr><th></th><th>v{aNo}</th><th>v{bNo}</th></tr></thead><tbody>{[["constraint_name", "name"], ["expected_outcome", "what it enforces"],
              ["business_reason", "meaning"], ["constraint_family", "rule type"], ["condition", "condition (text)"], ["predicate_template", "predicate (text)"]].map(([k, l]) =>
              (T(A, k) || T(B, k)) ? <tr key={k}><td className="mut">{l}</td><td>{T(A, k)}</td><td className={T(A, k) !== T(B, k) ? "chg" : ""}>{T(B, k)}</td></tr> : null)}</tbody></table>
          </details>
        </div>}
        <footer className="vc-f">
          {B && bNo !== active && <span className="mut">{same ? "Same logic — activating changes only the texts." : (ib.flagged != null && ia.flagged != null ? `Activating v${bNo} changes the flagged rows by ${sgn(dd(ia.flagged, ib.flagged))}.` : "")}</span>}
          <span style={{ flex: 1 }} />
          <button className="btn" onClick={onClose}>Close</button>
          {B && bNo !== active && <button className="btn pri" disabled={working} onClick={() => activate(bNo as number)}>{working ? "Switching…" : `Make v${bNo} active`}</button>}
        </footer>
      </div>
    </div>
  );
}

const fmtN = (n: any) => (n == null || isNaN(Number(n)) ? "—" : Number(n).toLocaleString());
const sgn = (n: any) => (n == null ? "—" : n === 0 ? "no change" : (n > 0 ? "+" : "") + Number(n).toLocaleString());
const canonL = (x: any): string => x == null ? "" : Array.isArray(x) ? "[" + x.map(canonL).join(",") + "]"
  : typeof x === "object" ? "{" + Object.keys(x).filter((k) => !(k === "numeric" && x[k] === false)).sort().map((k) => k + ":" + canonL(x[k])).join(",") + "}" : JSON.stringify(x);
function codesOf(lg: any): Set<string> {
  const out = new Set<string>();
  const walk = (c: any) => { if (!c || typeof c !== "object") return;
    (c.all || []).forEach(walk); (c.any || []).forEach(walk); if (c.not) walk(c.not);
    (Array.isArray(c.values) ? c.values : []).forEach((v: any) => out.add(String(v)));
    if (c.value != null && ["eq", "ne"].includes(String(c.op))) out.add(String(c.value));
    if (c.op === "matches" && typeof c.pattern === "string") { const m = c.pattern.match(/\(\?:([^()]*)\)/);
      if (m) m[1].split("|").map((x: string) => x.replace(/\\/g, "")).filter((x: string) => /^[A-Za-z0-9-]{1,6}$/.test(x)).forEach((x: string) => out.add(x)); } };
  if (lg) { walk(lg.when); walk(lg.then); walk(lg.assert); }
  return out;
}
function diffCodes(a: any, b: any) { const A = codesOf(a), B = codesOf(b);
  return { added: [...B].filter((x) => !A.has(x)).sort(), removed: [...A].filter((x) => !B.has(x)).sort(), kept: [...B].filter((x) => A.has(x)).length }; }

const VCSS = `
.vh-scrim{position:fixed;inset:0;z-index:93;background:rgba(14,36,54,.45);display:flex;align-items:center;justify-content:center;padding:16px}
.vc{background:#fff;width:min(1300px,100%);max-height:calc(100vh - 32px);display:flex;flex-direction:column;border-radius:10px;overflow:hidden;box-shadow:0 18px 50px rgba(0,0,0,.3);font-size:13px;color:#1d2b36}
.vc-h{display:flex;align-items:center;padding:10px 16px;background:linear-gradient(90deg,#460073 0%,#7500C0 55%,#A100FF 100%);border-bottom:3px solid #A100FF;color:#fff}
.vc-h>div{flex:1;display:flex;flex-direction:column}.vc-h .lbl{font-size:11px;letter-spacing:.08em;opacity:.85}.vc-h b{font-size:16px}.vc-h .sub{opacity:.8;font-size:12px}
.vc-h .x{background:none;border:0;color:#fff;font-size:22px;cursor:pointer}
.vc-b{padding:12px 16px;overflow:auto;flex:1;scrollbar-width:thin}
.vc-pick{display:flex;gap:16px;margin-bottom:10px}.vc-pick label{display:flex;gap:8px;align-items:center;font-weight:600}.vc-pick select{border:1px solid #cfd7de;border-radius:6px;padding:5px 8px;font:inherit;max-width:420px}
.vc-sec{border:1px solid #e6eaee;border-radius:10px;padding:10px 12px;margin-bottom:10px}.cond{background:#f8f9fb;border-radius:8px;padding:8px;font-family:monospace;font-size:12px}.cond div{margin:2px 0}
.vc-sec h4,.vc-sec summary{margin:0 0 8px;font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:#5b6b78;font-weight:700;cursor:pointer}
.two{display:grid;grid-template-columns:1fr 1fr;gap:12px}.two em{font-size:11px;color:#6b7a86;font-style:normal;display:block;margin-bottom:4px}
.codes{display:flex;flex-wrap:wrap;gap:6px;align-items:center;margin-bottom:8px}.code{font-family:monospace;padding:2px 8px;border-radius:10px;font-weight:700}.code.add{background:#e3f4ea;color:#1f6b40}.code.rem{background:#fde8e6;color:#9b2b1f;text-decoration:line-through}
.vc-t{width:100%;border-collapse:collapse}.vc-t th,.vc-t td{text-align:left;padding:5px 8px;border-bottom:1px solid #f0f2f4;vertical-align:top}.vc-t th{color:#5b6b78;font-weight:600}.vc-t td.chg{background:#fdf6e9}
.why{background:#f8f9fb;border-radius:8px;padding:8px}.why div{margin-top:3px}
.warn{color:#9b5b00}.mut{color:#6b7a86}.mono{font-family:monospace}
.vc-f{display:flex;gap:8px;align-items:center;padding:10px 16px;border-top:1px solid #e6eaee;background:#fafbfc}
.vc-f .btn{border:1px solid #cfd7de;background:#fff;border-radius:6px;padding:6px 12px;cursor:pointer;font:inherit}.vc-f .btn.pri{background:#7500C0;border-color:#7500C0;color:#fff}.vc-f .btn:disabled{opacity:.5}
`;
